import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, CheckCircle2, Lock } from "lucide-react";

/**
 * LEAN Phase Education card — 4 videos, one per phase.
 * Non-blocking: does not prevent access to any pillar.
 */
export default function PhaseEducationCard() {
  const [data, setData] = useState(null);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get("/lean/phase-video/current"),
        api.get("/lean/phase-videos"),
      ]);
      setData(c.data);
      setAll(a.data.videos || []);
    } catch (e) { /* silently skip if no block */ }
  };
  useEffect(() => { load(); }, []);

  if (!data || !data.video) return null;
  const v = data.video;
  const complete = data.completion_status === "COMPLETE";
  const inProgress = data.completion_status === "IN_PROGRESS";
  const published = v.video_status === "PUBLISHED" && v.video_url_or_asset_id;

  const markComplete = async () => {
    setBusy(true);
    try {
      await api.post("/lean/phase-video/complete", { phase: v.phase });
      toast.success("Phase lesson marked complete.");
      load();
    } catch (e) { toast.error("Could not mark complete."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="lean-phase-education">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white"
               style={{ background: v.accent }}>
            <Play size={16} />
          </div>
          <div>
            <div className="label-eyebrow" style={{ color: v.accent }}>Phase Education · {v.phase}</div>
            <div className="mt-1 font-head uppercase text-lg leading-tight">{v.video_title}</div>
          </div>
        </div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap">{v.duration}</div>
      </div>
      <p className="mt-3 text-sm text-foreground/80 max-w-3xl leading-relaxed" data-testid="lean-phase-education-desc">
        {v.video_description}
      </p>

      {/* Thumbnail / placeholder */}
      <div className="mt-4 relative rounded-md overflow-hidden aspect-video max-w-2xl border border-border"
           style={{ background: `linear-gradient(135deg, ${v.accent}20, ${v.accent}05)` }}>
        {v.thumbnail_asset ? (
          <img src={v.thumbnail_asset} alt={v.video_title} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center"
                 style={{ background: v.accent }}>
              {published ? <Play size={28} className="text-white ml-1" /> : <Lock size={24} className="text-white" />}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {published ? "Watch now" : "Video placeholder · Coming soon"}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {!published && (
          <button className="btn-primary opacity-60 cursor-not-allowed" disabled data-testid="lean-phase-video-cta">
            Coming Soon
          </button>
        )}
        {published && !complete && (
          <a href={v.video_url_or_asset_id} target="_blank" rel="noreferrer" className="btn-primary" data-testid="lean-phase-video-cta">
            <Play size={14} className="mr-1" /> Watch Now
          </a>
        )}
        {published && complete && (
          <>
            <div className="inline-flex items-center gap-2 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-3 py-1.5 text-xs font-head uppercase tracking-widest">
              <CheckCircle2 size={14} /> Phase Lesson Complete
            </div>
            <a href={v.video_url_or_asset_id} target="_blank" rel="noreferrer" className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="lean-phase-video-rewatch">
              Rewatch
            </a>
          </>
        )}
        {published && !complete && (
          <button onClick={markComplete} disabled={busy}
            className="text-xs underline text-muted-foreground hover:text-foreground disabled:opacity-50"
            data-testid="lean-phase-video-mark-complete">
            Mark as complete
          </button>
        )}
        {published && !complete && inProgress && (
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">In progress</span>
        )}
      </div>

      {/* Timeline — all 4 LEAN phase lessons (earlier stays accessible, future locked until unlock week) */}
      {all.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="label-eyebrow mb-3">All LEAN Phase Lessons</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lean-phase-education-timeline">
            {all.map((item) => {
              const isCurrent = item.phase === v.phase;
              const isComplete = item.completion_status === "COMPLETE";
              const isLocked = !item.unlocked;
              const itemPublished = item.video_status === "PUBLISHED" && item.video_url_or_asset_id;
              return (
                <div key={item.phase} className={`rounded-md border p-3 relative ${
                  isCurrent ? "ring-2 ring-offset-1 ring-offset-background" : ""
                } ${isLocked ? "opacity-50" : ""}`}
                  style={isCurrent ? { borderColor: item.accent, "--tw-ring-color": item.accent } : {}}
                  data-testid={`lean-phase-lesson-${item.phase}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: item.accent }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: item.accent }} /> {item.phase} · W{item.unlock_week}
                  </div>
                  <div className="mt-1.5 text-xs font-head uppercase leading-tight line-clamp-2">{item.video_title}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    {isLocked ? <><Lock size={10} /> Locked</> :
                     isComplete ? <><CheckCircle2 size={10} className="text-emerald-500" /> Complete</> :
                     itemPublished ? <><Play size={10} /> Watch</> :
                     "Coming Soon"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
